/**
 * Message components: the buttons and select menus bots attach to their messages, and the modal forms they open
 * in answer to a click or a command. People never send them; only the Bot API accepts them.
 */
import { z } from 'zod';
import { ID_PATTERN } from './constants';

export const COMPONENT_LIMITS = {
  rows: 5,
  buttonsPerRow: 5,
  customId: 100,
  buttonLabel: 80,
  placeholder: 150,
  options: 25,
  optionLabel: 100,
  optionValue: 100,
  optionDescription: 100,
  modalTitle: 45,
  modalFields: 5,
  fieldLabel: 45,
  fieldPlaceholder: 100,
  fieldValue: 4000,
} as const;

export const BUTTON_STYLES = ['primary', 'secondary', 'success', 'danger', 'link'] as const;
export type ButtonStyle = (typeof BUTTON_STYLES)[number];

const customId = z.string().min(1).max(COMPONENT_LIMITS.customId);

const linkUrl = z
  .string()
  .trim()
  .max(2048)
  .regex(/^https?:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i, 'Links must be full http:// or https:// addresses');

export const buttonSchema = z
  .object({
    type: z.literal('button'),
    style: z.enum(BUTTON_STYLES).default('secondary'),
    label: z.string().trim().min(1).max(COMPONENT_LIMITS.buttonLabel),
    /** What the bot is told when it is clicked. Link buttons have none; they open `url`. */
    customId: customId.optional(),
    url: linkUrl.optional(),
    disabled: z.boolean().default(false),
  })
  .superRefine((b, c) => {
    if (b.style === 'link') {
      if (!b.url) c.addIssue({ code: 'custom', path: ['url'], message: 'Link buttons need a url' });
      if (b.customId) c.addIssue({ code: 'custom', path: ['customId'], message: 'Link buttons have a url, not a customId' });
    } else {
      if (!b.customId) c.addIssue({ code: 'custom', path: ['customId'], message: 'Buttons need a customId' });
      if (b.url) c.addIssue({ code: 'custom', path: ['url'], message: 'Only link buttons have a url' });
    }
  });

export const selectOptionSchema = z.object({
  label: z.string().trim().min(1).max(COMPONENT_LIMITS.optionLabel),
  value: z.string().min(1).max(COMPONENT_LIMITS.optionValue),
  description: z.string().trim().min(1).max(COMPONENT_LIMITS.optionDescription).optional(),
  /** Picked when the menu is first shown. */
  default: z.boolean().default(false),
});

export const selectSchema = z
  .object({
    type: z.literal('select'),
    customId,
    placeholder: z.string().trim().min(1).max(COMPONENT_LIMITS.placeholder).optional(),
    options: z.array(selectOptionSchema).min(1).max(COMPONENT_LIMITS.options),
    minValues: z.number().int().min(0).max(COMPONENT_LIMITS.options).default(1),
    maxValues: z.number().int().min(1).max(COMPONENT_LIMITS.options).default(1),
    disabled: z.boolean().default(false),
  })
  .superRefine((s, c) => {
    if (s.minValues > s.maxValues) c.addIssue({ code: 'custom', path: ['minValues'], message: 'minValues is above maxValues' });
    if (s.maxValues > s.options.length) c.addIssue({ code: 'custom', path: ['maxValues'], message: 'maxValues is more than the number of options' });
    if (new Set(s.options.map((o) => o.value)).size !== s.options.length) c.addIssue({ code: 'custom', path: ['options'], message: 'Two options have the same value' });
    if (s.options.filter((o) => o.default).length > s.maxValues) c.addIssue({ code: 'custom', path: ['options'], message: 'More options are preselected than maxValues allows' });
  });

/**
 * Menus that pick a person, a role or a channel of the space instead of one of a fixed list. People see the space's own
 * members, roles or channels (a channel only if they can see it); the bot gets the ids and the full objects in `resolved`.
 */
export const ENTITY_SELECT_TYPES = ['user_select', 'role_select', 'channel_select'] as const;
export type EntitySelectType = (typeof ENTITY_SELECT_TYPES)[number];

function entitySelect<T extends EntitySelectType>(type: T) {
  return z
    .object({
      type: z.literal(type),
      customId,
      placeholder: z.string().trim().min(1).max(COMPONENT_LIMITS.placeholder).optional(),
      minValues: z.number().int().min(0).max(COMPONENT_LIMITS.options).default(1),
      maxValues: z.number().int().min(1).max(COMPONENT_LIMITS.options).default(1),
      disabled: z.boolean().default(false),
    })
    .superRefine((s, c) => {
      if (s.minValues > s.maxValues) c.addIssue({ code: 'custom', path: ['minValues'], message: 'minValues is above maxValues' });
    });
}

export const userSelectSchema = entitySelect('user_select');
export const roleSelectSchema = entitySelect('role_select');
export const channelSelectSchema = entitySelect('channel_select');

export const componentRowSchema = z
  .object({
    type: z.literal('row'),
    components: z.array(z.discriminatedUnion('type', [buttonSchema, selectSchema, userSelectSchema, roleSelectSchema, channelSelectSchema])).min(1),
  })
  .superRefine((row, c) => {
    const hasSelect = row.components.some((x) => x.type !== 'button');
    if (hasSelect && row.components.length > 1) c.addIssue({ code: 'custom', path: ['components'], message: 'A row holds one select menu, or up to 5 buttons' });
    if (!hasSelect && row.components.length > COMPONENT_LIMITS.buttonsPerRow) c.addIssue({ code: 'custom', path: ['components'], message: `A row holds at most ${COMPONENT_LIMITS.buttonsPerRow} buttons` });
  });

/** Up to five rows, and no two components with the same customId in one message. */
export const componentListSchema = z
  .array(componentRowSchema)
  .max(COMPONENT_LIMITS.rows, `A message can have at most ${COMPONENT_LIMITS.rows} rows of components`)
  .superRefine((rows, c) => {
    const seen = new Set<string>();
    for (const row of rows) {
      for (const comp of row.components) {
        if (!comp.customId) continue;
        if (seen.has(comp.customId)) c.addIssue({ code: 'custom', path: [], message: `Two components share the customId "${comp.customId}"` });
        seen.add(comp.customId);
      }
    }
  });

export type ComponentRowInput = z.input<typeof componentRowSchema>;
export type ComponentRow = z.output<typeof componentRowSchema>;
export type ButtonComponent = z.output<typeof buttonSchema>;
export type SelectComponent = z.output<typeof selectSchema>;
export type EntitySelectComponent = z.output<typeof userSelectSchema> | z.output<typeof roleSelectSchema> | z.output<typeof channelSelectSchema>;
export type MessageComponent = ButtonComponent | SelectComponent | EntitySelectComponent;

export const isEntitySelect = (c: MessageComponent): c is EntitySelectComponent => (ENTITY_SELECT_TYPES as readonly string[]).includes(c.type);

/** The component with this customId, if the rows have one that can be clicked. */
export function findComponent(rows: readonly ComponentRow[], id: string): MessageComponent | undefined {
  for (const row of rows) for (const comp of row.components) if (comp.customId === id) return comp;
  return undefined;
}

// ---- Modals ---------------------------------------------------------------------------

export const modalFieldSchema = z
  .object({
    customId,
    label: z.string().trim().min(1).max(COMPONENT_LIMITS.fieldLabel),
    style: z.enum(['short', 'paragraph']).default('short'),
    placeholder: z.string().trim().min(1).max(COMPONENT_LIMITS.fieldPlaceholder).optional(),
    required: z.boolean().default(true),
    minLength: z.number().int().min(0).max(COMPONENT_LIMITS.fieldValue).optional(),
    maxLength: z.number().int().min(1).max(COMPONENT_LIMITS.fieldValue).optional(),
    /** Filled in when the form opens. */
    value: z.string().max(COMPONENT_LIMITS.fieldValue).optional(),
  })
  .superRefine((f, c) => {
    if (f.minLength !== undefined && f.maxLength !== undefined && f.minLength > f.maxLength) c.addIssue({ code: 'custom', path: ['minLength'], message: 'minLength is above maxLength' });
    if (f.value !== undefined && f.maxLength !== undefined && f.value.length > f.maxLength) c.addIssue({ code: 'custom', path: ['value'], message: 'value is longer than maxLength' });
  });

export const modalSchema = z
  .object({
    customId,
    title: z.string().trim().min(1).max(COMPONENT_LIMITS.modalTitle),
    fields: z.array(modalFieldSchema).min(1).max(COMPONENT_LIMITS.modalFields),
  })
  .superRefine((m, c) => {
    if (new Set(m.fields.map((f) => f.customId)).size !== m.fields.length) c.addIssue({ code: 'custom', path: ['fields'], message: 'Two fields share a customId' });
  });

export type ModalInput = z.input<typeof modalSchema>;
export type ModalDefinition = z.output<typeof modalSchema>;
export type ModalField = z.output<typeof modalFieldSchema>;

/** What a person fills in: one text per field, by customId. */
export const modalSubmitSchema = z.object({
  values: z.record(z.string().max(COMPONENT_LIMITS.customId), z.string().max(COMPONENT_LIMITS.fieldValue)),
});

/** Checks a filled-in form against its definition: required fields, lengths and no fields that aren't there. */
export function checkModalValues(modal: Pick<ModalDefinition, 'fields'>, raw: Record<string, string>): { values: Record<string, string> } | { error: string } {
  const known = new Set(modal.fields.map((f) => f.customId));
  for (const key of Object.keys(raw)) if (!known.has(key)) return { error: 'The form has a field that does not exist.' };
  const values: Record<string, string> = {};
  for (const f of modal.fields) {
    const v = (raw[f.customId] ?? '').replace(/\r\n/g, '\n');
    if (f.style === 'short' && v.includes('\n')) return { error: `"${f.label}" must be on one line.` };
    if (!v.trim()) {
      if (f.required) return { error: `"${f.label}" is required.` };
      values[f.customId] = '';
      continue;
    }
    if (f.minLength !== undefined && v.length < f.minLength) return { error: `"${f.label}" needs at least ${f.minLength} characters.` };
    if (f.maxLength !== undefined && v.length > f.maxLength) return { error: `"${f.label}" can have at most ${f.maxLength} characters.` };
    values[f.customId] = v;
  }
  return { values };
}

/** Clicking a button or choosing from a select menu. */
export const componentInteractSchema = z.object({
  customId,
  values: z.array(z.string().max(COMPONENT_LIMITS.optionValue)).max(COMPONENT_LIMITS.options).default([]),
});

/** Checks what a person chose against the component they used. */
export function checkComponentChoice(component: MessageComponent, values: string[]): { values: string[] } | { error: string } {
  if (component.disabled) return { error: 'This component is turned off.' };
  if (component.type === 'button') {
    if (component.style === 'link') return { error: 'Link buttons open a page; they do not send anything to the bot.' };
    if (values.length) return { error: 'A button has no values.' };
    return { values: [] };
  }
  if (new Set(values).size !== values.length) return { error: 'The same choice was sent twice.' };
  if (component.type === 'select') {
    const allowed = new Set(component.options.map((o) => o.value));
    if (values.some((v) => !allowed.has(v))) return { error: 'That choice is not in the menu.' };
  } else if (values.some((v) => !ID_PATTERN.test(v))) {
    // Whether the person, role or channel is really in this space is for the server to check; the shape is checked here.
    return { error: 'That choice is not a valid id.' };
  }
  if (values.length < component.minValues) return { error: `Choose at least ${component.minValues}.` };
  if (values.length > component.maxValues) return { error: `Choose at most ${component.maxValues}.` };
  return { values };
}
